import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Paperclip, Loader2, History } from 'lucide-react';
import { MessageBubble } from './MessageBubble';

export const MessageThread = ({
  messages, loading, loadingOlder, hasMore, highlightId, userId, playingId, dragOver, showSender, seen, members,
  onRetry, onPlayed, onReact, onSnooze, onLoadOlder, onHighlightDone,
}) => {
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const firstScroll = useRef(true);
  const lastIdRef = useRef(null);
  const prependRef = useRef(null); // { scrollHeight, scrollTop } captured before older messages are added
  const [lightbox, setLightbox] = useState(null);

  // Auto-scroll only when a new message is appended (or on first load); keep position when older ones are prepended.
  useLayoutEffect(() => {
    if (loading) return;
    const el = containerRef.current;
    const lastId = messages[messages.length - 1]?.id || null;
    if (prependRef.current && el) {
      el.scrollTop = prependRef.current.scrollTop + (el.scrollHeight - prependRef.current.scrollHeight);
      prependRef.current = null;
    } else if (lastId !== lastIdRef.current) {
      endRef.current?.scrollIntoView({ block: 'end', behavior: firstScroll.current ? 'auto' : 'smooth' });
    }
    lastIdRef.current = lastId;
    if (messages.length) firstScroll.current = false;
  }, [messages, loading]);

  useEffect(() => {
    if (!highlightId) return undefined;
    containerRef.current?.querySelector(`[data-testid="message-${highlightId}"]`)?.scrollIntoView({ block: 'center' });
    const timer = setTimeout(() => onHighlightDone?.(), 2500);
    return () => clearTimeout(timer);
  }, [highlightId, onHighlightDone]);

  const loadOlder = () => {
    const el = containerRef.current;
    if (el) prependRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
    onLoadOlder();
  };

  return (
    <div className="thread-messages" ref={containerRef} data-testid="message-thread">
      {dragOver && (
        <div className="drop-overlay" data-testid="drop-overlay">
          <Paperclip size={28} /> Drop to send
        </div>
      )}
      {loading && <div className="thread-empty">Loading messages…</div>}
      {!loading && hasMore && (
        <button type="button" className="load-older" onClick={loadOlder} disabled={loadingOlder} data-testid="load-older-button">
          {loadingOlder ? <Loader2 size={14} className="spin" /> : <History size={14} />}
          {loadingOlder ? 'Loading…' : 'Load older messages'}
        </button>
      )}
      {!loading && messages.length === 0 && (
        <div className="thread-empty" data-testid="thread-empty">
          No messages yet. Say hello, or hold the talk button below.
        </div>
      )}
      {messages.map((m) => (
        <MessageBubble
          key={m._key || m.id}
          msg={m}
          mine={m.from_user_id === userId}
          live={playingId === m.id}
          highlighted={highlightId === m.id}
          showSender={showSender}
          seen={seen?.id === m.id ? seen : null}
          members={members}
          channel={showSender}
          userId={userId}
          onRetry={onRetry}
          onPlayed={onPlayed}
          onReact={onReact}
          onSnooze={onSnooze}
          onOpenImage={setLightbox}
        />
      ))}
      <div ref={endRef} />
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)} data-testid="image-lightbox">
          <img src={lightbox} alt="Full size attachment" />
        </div>
      )}
    </div>
  );
};
